<template>
  <BaseDialog v-model="dialog" maxWidth="1000" title="Workorder">
    <div class="workorder-info">
      <table>
        <tr v-for="(_, i) in fields.filter((_, i) => i % 2 === 0)" :key="i">
          <td class="info-title">{{ fields[i * 2].title }}</td>
          <td class="info-value">
            <span v-if="fields[i * 2].format">
              {{ fields[i * 2].format(workorder[fields[i * 2].key]) }}
            </span>
            <span v-else> {{ workorder[fields[i * 2].key] }}</span>
          </td>
          <td class="info-title" v-if="i * 2 + 1 < fields.length">
            {{ fields[i * 2 + 1].title }}
          </td>
          <td class="info-title" v-else />
          <td class="info-value" v-if="i * 2 + 1 < fields.length">
            <span v-if="fields[i * 2 + 1].format">
              {{ fields[i * 2 + 1].format(workorder[fields[i * 2 + 1].key]) }}
            </span>
            <span v-else> {{ workorder[fields[i * 2 + 1].key] }}</span>
          </td>
          <td class="info-value" v-else />
        </tr>
      </table>
    </div>

    <NumberField
      class="mt-4"
      :model-value="workorder.hour.toNumber()"
      text-align="left"
      label="Hour"
      variant="outlined"
      density="compact"
      required
      @update:modelValue="workorder.hour = new Decimal($event)"
    />

    <NumberField
      :model-value="workorder.cost.toNumber()"
      text-align="left"
      label="Cost"
      prefix="$"
      variant="outlined"
      density="compact"
      required
      @update:modelValue="workorder.cost = new Decimal($event)"
    />

    <template #actions>
      <v-btn :loading="loading" @click="save">Save</v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import BaseDialog from "../base/dialogs/BaseDialog.vue";
import { DateObject, Workorder } from "@/classes";
import NumberField from "../base/inputs/NumberField.vue";
import Decimal from "decimal.js";

onMounted(() => console.log);

const emits = defineEmits(["change"]);

const dialog = ref(false);
const workorder = ref<Workorder>(new Workorder());
const loading = ref(false);

const open = async (id: string | null) => {
  dialog.value = true;
  if (id) {
    workorder.value = await Workorder.get(id);
  } else {
    workorder.value = new Workorder();
  }
};

const fields: {
  title: string;
  key: keyof Workorder;
  format?: (val: any) => string;
}[] = [
  { title: "Order Type", key: "order_type" },
  { title: "Notification", key: "notification" },
  { title: "Order", key: "order" },
  {
    title: "Basic Start Date",
    key: "basic_start_date",
    format: (val: any) => (val instanceof DateObject ? val.toString() : val),
  },
  { title: "Statutory Flag", key: "statutory_flag_display" },
  { title: "Functional Location", key: "functional_location" },
  { title: "Description", key: "description" },
  { title: "System Status", key: "system_status" },
  { title: "User Status", key: "user_status" },
  { title: "Main Work Center", key: "main_work_center" },
  { title: "Planner Group", key: "planner_group" },
  { title: "Maintenance Plan", key: "maintenance_plan" },
  { title: "Maintenance Item", key: "maintenance_item" },
  {
    title: "Created On",
    key: "created_on",
    format: (val: any) => (val instanceof DateObject ? val.toString() : val),
  },
];

const save = async () => {
  loading.value = true;
  await workorder.value.save();
  loading.value = false;
  dialog.value = false;
  emits("change");
};

defineExpose({ open });
</script>

<style lang="scss" scoped>
.workorder-info {
  display: flex;
  table {
    width: 100%;
    border-collapse: collapse;
  }
  td,
  th {
    border: 1px solid #dddddd;
    padding: 8px;
  }
  tr:nth-child(even) {
    background-color: #eeeeee;
  }

  .info-title {
    text-align: right;
    font-weight: 600;
  }
  .info-value {
    text-align: left;
  }
}
</style>
